import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { BiReport } from '@app/core/model/entities/bi-report/bi-report';
import { OrganizationWithLogo } from '@app/core/model/entities/organization/organization';
import { balanceItemsInArrays } from '@app/shared/extra/utils';
import { OrganizationsService } from '@app/shared/services/organizations.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { EMPTY, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'organizations',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss'],
  standalone: false
})
export class OrganizationsComponent implements OnInit {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly accessManager = inject(AccessManager);
  private readonly organizationsService = inject(OrganizationsService);
  private readonly appManager = inject(AppManager);
  private readonly router = inject(Router);

  public userBiReports$: Observable<BiReport[]>;

  /**
   * An Observable list of organization row with their associated logos.
   */
  public organizationsRowWithLogo$: Observable<OrganizationWithLogo[][]>;

  private destroyRef = inject(DestroyRef);

  /**
   * Fetch all accessible Organizations with their associated logos from service.
   */
  public ngOnInit(): void {
    this.organizationsRowWithLogo$ = this.organizationsService.loadOrganizationsWithLogo().pipe(
      map(organizations => balanceItemsInArrays(organizations, 5)),
      takeUntilDestroyed(this.destroyRef)
    );

    if (this.accessManager.hasAccess(PermissionEnum.VIEW_BI_REPORT, true)) {
      this.userBiReports$ = this.accessManager.userBiReports$;
    } else {
      this.userBiReports$ = EMPTY;
    }
  }

  /**
   * Navigate to the selected Organization dashboard page.
   * @param organizationId Id of the organization.
   */
  public async goToOrganization(organizationId: string): Promise<void> {
    // load all data related to the organization.
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.ORGANIZATION_LIST,
      NavigateToEnum.DASHBOARD,
      EntityTypeEnum.ORGANIZATION,
      organizationId
    );
    // Navigate to the organization's dashboard.
    await this.router.navigate(['organization', organizationId]);
  }

  /**
   * Navigate to one of the user Power BI reports.
   * @param biReport User BI report to navigate to.
   */
  public async navigateToUserBiReport(biReport: BiReport): Promise<void> {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.BUTTON,
      NavigateToEnum.DASHBOARD,
      EntityTypeEnum.BI_REPORT,
      biReport.name
    );

    await this.router.navigate(['report', biReport.id]);
  }
}
